import React, { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { loginRequest } from '../../authConfig';
import { 
  Box, 
  Button, 
  Typography, 
  Alert, 
  Divider,
  Stack,
  CircularProgress
} from '@mui/material';
import { 
  Microsoft as MicrosoftIcon,
  Email as EmailIcon 
} from '@mui/icons-material';
import EmailLoginForm from './EmailLoginForm';
import OTPVerificationForm from './OTPVerificationForm';

type AuthMethod = 'microsoft' | 'otp' | null;
type OTPStep = 'email' | 'verify';

interface AuthMethodSelectorProps {
  onSuccess?: () => void;
  redirectPath?: string;
}

const AuthMethodSelector: React.FC<AuthMethodSelectorProps> = ({ 
  onSuccess, 
  redirectPath 
}) => {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [selectedMethod, setSelectedMethod] = useState<AuthMethod>(null);
  const [otpStep, setOtpStep] = useState<OTPStep>('email');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleMicrosoftLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Store redirect path if provided
      if (redirectPath) {
        sessionStorage.setItem('redirectAfterLogin', redirectPath);
      }
      
      await instance.loginPopup(loginRequest);
      onSuccess?.();
    } catch (err) {
      console.error('Microsoft login failed:', err);
      setError('Failed to sign in with Microsoft. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPRequest = (userEmail: string) => {
    setEmail(userEmail);
    setOtpStep('verify');
  };

  const handleOTPSuccess = () => {
    // Store redirect path if provided
    if (redirectPath) {
      navigate(redirectPath);
    } else {
      onSuccess?.();
    }
  };

  const handleBackToMethods = () => {
    setSelectedMethod(null);
    setOtpStep('email');
    setEmail('');
    setError(null);
  };

  const handleBackToEmail = () => {
    setOtpStep('email');
  };

  // Custom Microsoft icon component
  const MicrosoftLogo = () => (
    <svg width="20" height="20" viewBox="0 0 21 21">
      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  );

  // If no method is selected, show the selector
  if (!selectedMethod) {
    return (
      <Box sx={{ maxWidth: 400, mx: 'auto' }}>
        <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 600 }}>
          Sign in to MarketCompass
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Stack spacing={2}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => {
              setSelectedMethod('microsoft');
              handleMicrosoftLogin();
            }}
            disabled={loading}
            startIcon={<MicrosoftLogo />}
            sx={{
              bgcolor: '#2563eb',
              '&:hover': { bgcolor: '#1e40af' },
              py: 1.5,
              textTransform: 'none',
              fontSize: '1rem',
            }}
          >
            Sign in with Horwath HTL
          </Button>
          
          <Divider>
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>
          
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => setSelectedMethod('otp')}
            disabled={loading}
            startIcon={<EmailIcon />}
            sx={{
              bgcolor: '#16a34a',
              '&:hover': { bgcolor: '#15803d' },
              py: 1.5,
              textTransform: 'none',
              fontSize: '1rem',
            }}
          >
            Sign in with Email
          </Button>
        </Stack>
        
        <Typography 
          variant="caption" 
          align="center" 
          display="block" 
          sx={{ mt: 3, color: 'text.secondary' }}
        >
          By signing in, you agree to our Terms of Service and Privacy Policy
        </Typography>
      </Box>
    );
  }

  // If OTP method is selected
  if (selectedMethod === 'otp') {
    if (otpStep === 'email') {
      return (
        <EmailLoginForm
          onSuccess={handleOTPRequest}
          onBack={handleBackToMethods}
        />
      );
    } else {
      return (
        <OTPVerificationForm
          email={email}
          onSuccess={handleOTPSuccess}
          onBack={handleBackToEmail}
        />
      );
    }
  }

  // Microsoft login is handled by the popup, so we just show a loading state
  if (selectedMethod === 'microsoft' && loading) {
    return (
      <Box sx={{ maxWidth: 400, mx: 'auto', textAlign: 'center', py: 4 }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography color="text.secondary">
          Signing in with Horwath HTL...
        </Typography>
      </Box>
    );
  }

  return null;
};

export default AuthMethodSelector;